import { format, isValid, parseISO } from 'date-fns';

import type {
  AdminStorefrontDetail,
  AdminStorefrontEvent,
} from '@/lib/api/storefrontAdminHooks';

import { isReviewPolicy, reviewPolicyOption } from './reviewPolicy';
import { planLabel } from './storefrontFormat';

/*
 * History in plain words. The backend records one `change` event per field
 * an owner or admin edited (`field`, `old_value`, `new_value`, a raw
 * `note`) and one `review` event per review or status action. These
 * helpers turn the field keys ("design.hero.headline",
 * "products.nad-injection-200mg-5ml.monthly", "promo_codes.HALF50") into
 * labels, values into readable text (dates, prices, on/off) and photo URLs
 * into images, for the History panel and the "changed since you opened
 * it" notice.
 */

const SECTION: Record<string, string> = {
  hero: 'Hero',
  trust: 'Trust strip',
  how_it_works: 'How it works',
  products: 'Products section',
  featured_product: 'Featured product',
  about: 'About',
  lifestyle: 'Lifestyle',
  faq: 'FAQ',
};

const SECTION_FIELD: Record<string, string> = {
  headline: 'headline',
  subheadline: 'subheadline',
  cta_label: 'button text',
  title: 'title',
  body: 'text',
  blurb: 'blurb',
  image_id: 'image',
  photo_url: 'photo',
  photo_focus: 'photo crop',
  enabled: 'shown',
  canonical_product_id: 'product',
  items: 'questions',
};

const TOP_FIELD: Record<string, string> = {
  name: 'Store name',
  slug: 'Store address',
  headline: 'Headline',
  subheadline: 'Subheadline',
  support_email: 'Support email',
  accent_color: 'Accent color',
  logo_url: 'Logo',
  creator_name: 'Creator name',
  medical_group_name: 'Medical group',
  'design.template': 'Template',
  'design.theme.primary': 'Primary color',
  'design.theme.secondary': 'Secondary color',
  'design.product_image_style': 'Product image style',
  'design.sections': 'Section order',
  'design.order': 'Section order',
  'design.byline.mode': 'Byline',
  'design.byline.text': 'Byline text',
  review_policy: 'Review policy',
};

export type ChangeKind = 'photo' | 'price' | 'coupon' | 'payout' | 'copy' | 'design' | 'other';

export interface HumanChange {
  label: string;
  kind: ChangeKind;
  /** Readable old and new values (null = empty / not set). */
  from: string | null;
  to: string | null;
  /** Image URLs for a photo or logo change. */
  fromImage: string | null;
  toImage: string | null;
}

function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

function isImageUrl(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^https:\/\/\S+\.(?:png|jpe?g|webp)(?:\?\S*)?$/i.test(value);
}

/** "$189.00" → "$189"; keeps cents when they are not zero. */
function tidyMoney(text: string): string {
  return text.replace(/\$(\d[\d,]*)\.00\b/g, '$$$1');
}

/** ISO dates in a value ("2026-10-12 to 2026-10-18") → "Oct 12 – Oct 18, 2026". */
function tidyDates(text: string): string {
  const range = /(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})/.exec(text);
  if (range) {
    const a = parseISO(range[1]);
    const b = parseISO(range[2]);
    if (isValid(a) && isValid(b)) {
      const sameYear = a.getFullYear() === b.getFullYear();
      const label = `${format(a, sameYear ? 'MMM d' : 'MMM d, yyyy')} – ${format(b, 'MMM d, yyyy')}`;
      text = text.replace(range[0], label);
    }
  }
  return text.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (iso) => {
    const date = parseISO(iso);
    return isValid(date) ? format(date, 'MMM d, yyyy') : iso;
  });
}

/** The byline's mode as the history records it (`design.byline.mode`). */
const BYLINE_MODE: Record<string, string> = {
  default: 'template line',
  custom: 'own words',
  hidden: 'hidden',
};

/** A `review_policy_changed` note ("Review policy: copy_and_images →
 *  every_change · {admin note}") with the two policies in words. Only the
 *  leading pair is read, never the admin's own words. */
export function readablePolicyNote(note: string): string {
  return note.replace(
    /^(Review policy: )([a-z_]+) → ([a-z_]+)/,
    (whole, head: string, from: string, to: string) =>
      isReviewPolicy(from) && isReviewPolicy(to)
        ? `${head}${reviewPolicyOption(from).label} → ${reviewPolicyOption(to).label}`
        : whole,
  );
}

function readable(field: string, value: string | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (field === 'design.byline.mode' && BYLINE_MODE[value]) return BYLINE_MODE[value];
  if (field === 'review_policy' && isReviewPolicy(value)) return reviewPolicyOption(value).label;
  if (isImageUrl(value)) return 'photo';
  if (field.endsWith('.enabled')) {
    return value === 'True' || value === 'true' ? 'shown' : value === 'False' || value === 'false' ? 'hidden' : value;
  }
  if (value === 'True' || value === 'true') return 'on';
  if (value === 'False' || value === 'false') return 'off';
  return tidyDates(tidyMoney(value));
}

/** A change event's field as a label: "Hero headline", "NAD+ · Monthly
 *  price", "Coupon HALF50". `productNames` maps canonical ids to names. */
export function fieldLabel(field: string, productNames: Record<string, string> = {}): {
  label: string;
  kind: ChangeKind;
} {
  if (TOP_FIELD[field]) {
    const kind: ChangeKind =
      field === 'logo_url' ? 'photo' : field.startsWith('design.') || field === 'accent_color' ? 'design' : 'copy';
    return { label: TOP_FIELD[field], kind };
  }
  const parts = field.split('.');
  if (parts[0] === 'design' && parts.length >= 3) {
    const section = SECTION[parts[1]] ?? capitalize(parts[1].replace(/_/g, ' '));
    const sub = parts.slice(2).join('.');
    // "Featured product: Glutathione", not "Featured product product".
    if (sub === 'canonical_product_id') return { label: section, kind: 'copy' };
    const what = SECTION_FIELD[sub] ?? sub.replace(/_/g, ' ');
    const kind: ChangeKind = sub === 'photo_url' || sub === 'photo_focus' ? 'photo' : sub === 'image_id' || sub === 'enabled' ? 'design' : 'copy';
    return { label: `${section} ${what}`, kind };
  }
  if (parts[0] === 'products' && parts.length >= 2) {
    const id = parts[1];
    const plan = parts[2];
    const name = productNames[id] ?? id;
    return { label: plan ? `${name} · ${planLabel(plan, null)} price` : name, kind: 'price' };
  }
  if (parts[0] === 'promo_codes' && parts[1]) return { label: `Coupon ${parts[1]}`, kind: 'coupon' };
  if (parts[0] === 'payouts') return { label: 'Payout', kind: 'payout' };
  return { label: capitalize(field.replace(/[._]/g, ' ')), kind: 'other' };
}

/** A canonical product id as its name: the store's own product name, else
 *  the id in words ("glutathione-injection-200mg-10ml" → "Glutathione
 *  injection 200mg 10ml"). */
function productName(id: string | null, productNames: Record<string, string>): string | null {
  if (!id) return null;
  return productNames[id] ?? capitalize(id.replace(/[-_]+/g, ' '));
}

export function humanChange(
  event: Pick<AdminStorefrontEvent, 'field' | 'old_value' | 'new_value'>,
  productNames: Record<string, string> = {},
): HumanChange {
  const field = event.field ?? '';
  const { label, kind } = fieldLabel(field, productNames);
  if (field.endsWith('.canonical_product_id')) {
    return {
      label,
      kind,
      from: productName(readable(field, event.old_value), productNames),
      to: productName(readable(field, event.new_value), productNames),
      fromImage: null,
      toImage: null,
    };
  }
  return {
    label,
    kind,
    from: readable(field, event.old_value),
    to: readable(field, event.new_value),
    fromImage: isImageUrl(event.old_value) ? event.old_value : null,
    toImage: isImageUrl(event.new_value) ? event.new_value : null,
  };
}

/** One line for a change: "Hero headline: “A” → “B”", "About photo: new photo". */
export function changeSentence(change: HumanChange): string {
  if (change.kind === 'photo') {
    if (!change.to) return `${change.label} removed`;
    return `${change.label}: ${change.from ? 'replaced' : 'added'}`;
  }
  if (!change.from && change.to) return `${change.label} set to “${change.to}”`;
  if (change.from && !change.to) return `${change.label} cleared`;
  return `${change.label}: “${change.from ?? ''}” → “${change.to ?? ''}”`;
}

export function productNameMap(store: Pick<AdminStorefrontDetail, 'products'>): Record<string, string> {
  return Object.fromEntries(
    store.products.map((p) => [p.canonical_product_id, p.name || p.canonical_product_id]),
  );
}

export const EVENT_LABEL: Record<string, string> = {
  submitted: 'Submitted for review',
  resubmitted: 'Resubmitted',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  rejected: 'Rejected',
  paused: 'Paused',
  unpaused: 'Unpaused',
  invite_redeemed: 'Invite redeemed, store created',
  edited: 'Edited',
  changes_published: 'Changes published',
  changes_submitted: 'Changes sent for review',
  changes_approved: 'Changes approved',
  changes_rejected: 'Changes rejected',
  reverted: 'Reverted to the last approved version',
  marked_reviewed: 'Marked as reviewed',
  review_mode_changed: 'Review setting changed',
  review_policy_changed: 'Review policy changed',
  commerce_published: 'Products, prices or codes live without review',
  payout_created: 'Payout recorded',
  payout_paid: 'Payout marked paid',
  payout_voided: 'Payout voided',
  payout_updated: 'Payout updated',
};

/** A backend-written history note with its raw field keys in words:
 *  "Changes sent for review (design.hero.headline, accent_color)" reads
 *  "Changes sent for review (Hero headline, Accent color)". A note whose
 *  brackets hold anything but field keys is kept as written. */
export function readableNote(note: string, productNames: Record<string, string> = {}): string {
  return note.replace(/\(([^()]+)\)\s*$/, (whole, inner: string) => {
    const [list, more] = inner.split(/ and (?=\d+ more$)/);
    const keys = list.split(/,\s*/);
    const isKey = (key: string) =>
      Boolean(TOP_FIELD[key]) || /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*(?:\.[A-Za-z0-9_-]+)+$|^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(key);
    if (!keys.every((key) => isKey(key.trim()))) return whole;
    const labels = [...new Set(keys.map((key) => fieldLabel(key.trim(), productNames).label))];
    return `(${labels.join(', ')}${more ? ` and ${more}` : ''})`;
  });
}

export function eventKey(event: AdminStorefrontEvent, index = 0): string {
  return event.id ?? `${event.action}|${event.created_at}|${event.field ?? ''}|${index}`;
}

/** What one event says, in a line ("Approved", "Hero headline: “A” → “B”"). */
export function eventSentence(
  event: AdminStorefrontEvent,
  productNames: Record<string, string> = {},
): string {
  if (event.kind === 'change' && event.field) {
    if (event.action.startsWith('payout_')) {
      return `${EVENT_LABEL[event.action] ?? 'Payout'}: ${readable('', event.new_value) ?? ''}`.trim();
    }
    return changeSentence(humanChange(event, productNames));
  }
  return EVENT_LABEL[event.action] ?? capitalize(event.action.replace(/_/g, ' '));
}
